import { useCallback, useEffect, useRef, useState } from 'react';
import { effectiveLeadsScopeSignature } from '../utils/effectiveLeadsLoader';

const initialState = (signature, status = 'idle', error = null) => ({
  signature, status, error, rows: status === 'idle' ? null : [], loaded: 0, total: null, loadId: 0,
});

// Only complete results are published. Pending pages remain local to the
// loader so cancellation/failure can never expose a partially exportable set.
export default function useEffectiveLeadsLoad(scope, fetchAll) {
  const signature = effectiveLeadsScopeSignature(scope);
  const currentScope = useRef(signature);
  currentScope.current = signature;
  const active = useRef(null);
  const generation = useRef(0);
  const mounted = useRef(false);
  const [state, setState] = useState(() => initialState(signature));

  useEffect(() => {
    mounted.current = true;
    setState((previous) => previous.signature === signature ? previous
      : initialState(signature, previous.status === 'idle' ? 'idle' : 'incomplete', 'Scope changed. Load the current scope.'));
    return () => {
      mounted.current = false;
      active.current?.controller.abort();
      active.current = null;
    };
  }, [signature]);

  const cancel = useCallback(() => {
    const run = active.current;
    if (!run) return;
    active.current = null;
    run.controller.abort();
    setState((previous) => ({ ...previous, rows: [], status: 'incomplete', error: 'Loading cancelled. Results are incomplete.' }));
  }, []);

  const reset = useCallback(() => {
    active.current?.controller.abort();
    active.current = null;
    setState(initialState(currentScope.current));
  }, []);

  const load = useCallback(async () => {
    if (active.current?.signature === signature) return null;
    active.current?.controller.abort();
    const run = { signature, controller: new AbortController(), loadId: ++generation.current };
    active.current = run;
    const isCurrent = () => mounted.current && active.current === run && currentScope.current === signature && !run.controller.signal.aborted;
    setState({ ...initialState(signature, 'loading'), loadId: run.loadId });
    try {
      const result = await fetchAll(JSON.parse(signature), {
        signal: run.controller.signal,
        onProgress: ({ loaded, total }) => {
          if (isCurrent()) setState((previous) => ({ ...previous, loaded, total }));
        },
      });
      if (!isCurrent()) return null;
      setState({ signature, status: 'complete', rows: result.rows, loaded: result.rows.length,
        total: result.pagination.total, error: null, loadId: run.loadId });
      return result;
    } catch (error) {
      if (isCurrent()) setState((previous) => ({ ...previous, rows: [], status: 'incomplete',
        error: error.message || 'Loading failed. Results are incomplete.' }));
      return null;
    } finally {
      if (active.current === run) active.current = null;
    }
  }, [signature, fetchAll]);

  // Hide a previous scope immediately during render, before effect cleanup.
  const visible = state.signature === signature ? state
    : initialState(signature, state.status === 'idle' ? 'idle' : 'incomplete', 'Scope changed. Load the current scope.');
  return { ...visible, load, cancel, reset };
}
